import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLabels, useRequestData, usePrefs } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { isBrowser } from 'ws-scripts/modules/environment';
import { VehicleViews } from 'wsm-vehicle-views';
import { ErrorBoundary } from 'wsm-error-boundary';

import { getVehicleTitle, remapPrefs } from '../utils/utility';
import { HighlightedAttributeFactory } from './HighlightedAttributeFactory';
import { AdditionalDetails } from './AdditionalDetails';
import { useHighlightedAttributes } from '../hooks/useHighlightedAttributes';
import { sortQuickNotes } from '../utils/sortQuickNotes';

/**
 * Main VehicleTitle Component
 */
const VehicleTitle = ({ isInSidebar = false, hasViewsData, numberOfViews }) => {
	const labels = useLabels();
	const { deviceType, locale } = useRequestData();
	const unmappedPrefs = usePrefs();

	// Remap prefs for sidebar
	const prefs = isInSidebar
		? remapPrefs(unmappedPrefs, '-sidebar')
		: unmappedPrefs;

	// Get Redux state
	const storeData = useSelector((state) => ({
		title: state.title,
		vehicle: state.vehicle,
		highlightedAttrsConfig: state.highlightedAttrsConfig
	}));

	const { title, vehicle, highlightedAttrsConfig } = storeData;

	// Get highlighted attributes configuration
	const { attributeConfigs, conditions, hasAdditionalDetails } =
		useHighlightedAttributes({
			vehicle,
			highlightedAttrsConfig,
			forceShowVehicleCondition: prefs.forceShowVehicleCondition,
			labels,
			prefs,
			hasViewsData,
			numberOfViews,
			locale
		});

	// Create highlighted attributes
	const attributes = [];
	const quickNotes = [];

	// Build attributes from configs
	attributeConfigs.forEach((config) => {
		const attr = (
			<HighlightedAttributeFactory
				type={config.type}
				content={config.content}
				badgeClasses={config.badgeClasses}
			/>
		);

		attributes.push(attr);
		quickNotes.push({
			type: config.type,
			highlightedAttr: attr,
			note: config.note
		});
	});

	// Add vehicle views if applicable
	if (conditions.shouldShowVehicleViews) {
		// Skip if views don't meet minimum threshold
		if (numberOfViews && numberOfViews >= prefs.vehicleViews_minimumViews) {
			const numberOfDays = Number(
				prefs?.vehicleViews_timeFrame?.replace(/[^0-9]/g, '')
			);
			const daysAlias =
				numberOfDays && numberOfDays === 1 ? 'DAY' : 'DAYS';
			const vehicleViewsContent = (
				<ErrorBoundary newRelicPageAction="ws-vehicle-title VehicleViews error boundary">
					<VehicleViews
						vehicleViews={numberOfViews}
						displayStyle={prefs.vehicleViews_displayStyle}
						minimumViews={prefs.vehicleViews_minimumViews}
						timeFrame={prefs.vehicleViews_timeFrame}
					/>
				</ErrorBoundary>
			);

			const vehicleViewsAttribute = (
				<HighlightedAttributeFactory
					type="vehicleViews"
					content={vehicleViewsContent}
				/>
			);

			attributes.push(vehicleViewsAttribute);
			quickNotes.push({
				type: 'vehicleViews',
				highlightedAttr: vehicleViewsAttribute,
				note: `${numberOfViews} ${labels.get(
					'HOTCAR_VIEWS_IN_THE_PAST'
				)} ${numberOfDays} ${labels.get(daysAlias)}`
			});
		}
	}

	const highlightedAttributes = { attributes, quickNotes };

	// SEO subtitle
	const SeoSubTitleLine = vehicle.seoSubTitle.length ? (
		<span
			className={setClassNames([
				'additional-details',
				'd-block',
				'mt-0',
				'mb-1',
				'font-weight-normal',
				prefs.titleDetailsSize,
				prefs.titleDetailsColor
			])}
		>
			{vehicle.seoSubTitle}
		</span>
	) : null;

	// Get vehicle title
	const vehicleTitle = getVehicleTitle(
		title,
		labels,
		vehicle.type,
		vehicle.certified,
		prefs.forceShowVehicleCondition,
		prefs.showVehicleTitleSeparators,
		prefs.vehicleTitleSeparator
	);

	// Publish vehicle title to pubsub
	useEffect(() => {
		const pubsub = isBrowser && window?.DDC?.pubsub;
		if (pubsub && vehicleTitle) {
			pubsub?.publishDelayed(
				'ws-vehicle-toolbar/show-vehicle-title',
				vehicleTitle
			);
		}
	}, [vehicleTitle]);

	// Early return if no title
	if (Object.keys(title).length === 0 && title.constructor === Object) {
		return null;
	}

	// Sort quick notes to match render order
	const sortedQuickNotes = sortQuickNotes(
		highlightedAttributes.attributes,
		highlightedAttributes.quickNotes
	);

	// Helper to decode HTML entities
	const decodeMarkup = (encodedTitle) => ({ __html: `${encodedTitle}` });

	return (
		<>
			{prefs.enableLineBreak === 'false' ? (
				<h1 className={setClassNames(['vehicle-title', 'h2', 'm-0'])}>
					<span
						dangerouslySetInnerHTML={decodeMarkup(
							vehicleTitle.join(' ')
						)}
					/>
					{SeoSubTitleLine}
				</h1>
			) : (
				<h1
					className={setClassNames([
						'vehicle-title',
						'm-0',
						'line-height-reset',
						deviceType !== 'DESKTOP' && 'h2'
					])}
				>
					<span
						className={setClassNames([
							'd-block',
							prefs.titleHeading,
							prefs.titleHeadingColor,
							prefs.titleHeadingWeight,
							prefs.titleHeadingSize
						])}
						dangerouslySetInnerHTML={decodeMarkup(
							vehicleTitle.length > 1 && vehicleTitle[0]
						)}
					/>
					<span
						className={setClassNames([
							prefs.titleHeadingColor,
							prefs.titleHeadingTagLineWeight,
							prefs.titleHeadingTagLineSize
						])}
						dangerouslySetInnerHTML={decodeMarkup(
							vehicleTitle.length > 1
								? vehicleTitle[1]
								: vehicleTitle[0]
						)}
					/>
					{SeoSubTitleLine}
				</h1>
			)}
			<AdditionalDetails
				attributes={highlightedAttributes.attributes}
				quickNotesAttrs={sortedQuickNotes}
				prefs={prefs}
				hasAdditionalDetails={hasAdditionalDetails}
			/>
		</>
	);
};

VehicleTitle.propTypes = {
	isInSidebar: PropTypes.bool,
	hasViewsData: PropTypes.bool.isRequired,
	numberOfViews: PropTypes.number.isRequired
};

export default VehicleTitle;
